import React from 'react';
import { ChevronDown, ChevronUp, Folder, FolderOpen, Tag, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { TalentKategori } from '../../services/api';
import SelectableTalentList, { TalentSelection } from './SelectableTalentList';

interface SelectableTalentTreeProps {
  kategorier: TalentKategori[];
  selectionsByKategori: Map<number, TalentSelection[]>;
  apneKategorier: Set<number>;
  onToggle: (id: number) => void;
  onTalentToggle: (kategoriId: number, index: number) => void;
  onAntallChange: (kategoriId: number, index: number, antall: number) => void;
  onBeskrivelseChange: (kategoriId: number, index: number, beskrivelse: string) => void;
  onClearKategori: (kategoriId: number) => void;
  getChildren: (parentId: number) => TalentKategori[];
}

const SelectableTalentTree: React.FC<SelectableTalentTreeProps> = ({
  kategorier,
  selectionsByKategori,
  apneKategorier,
  onToggle,
  onTalentToggle,
  onAntallChange,
  onBeskrivelseChange,
  onClearKategori,
  getChildren,
}) => {
  const renderKategori = (kategori: TalentKategori, level: number): React.ReactElement => {
    const children = getChildren(kategori.id);
    const selections = selectionsByKategori.get(kategori.id) || [];
    const harChildren = children.length > 0;
    const harTalenter = selections.length > 0;
    const selectedCount = selections.filter((s) => s.selected).length;
    const erValgt = selectedCount > 0;

    const bgClass = erValgt
      ? level === 0
        ? 'bg-primary hover:bg-primary/90'
        : level === 1
          ? 'bg-secondary hover:bg-secondary/90 text-secondary-foreground'
          : 'bg-sky-400 hover:bg-sky-500'
      : 'bg-gray-400 hover:bg-gray-500';

    const textColorClass = erValgt && level === 1 ? '' : 'text-white';

    const Icon =
      level === 0 ? Folder : level === 1 ? FolderOpen : Tag;
    const iconSizeClass = level === 0 ? 'h-5 w-5' : 'h-4 w-4';

    return (
      <div key={kategori.id} className="mb-2">
        <div
          className={cn(
            'flex items-center gap-3 rounded-md cursor-pointer transition-all',
            level === 0 ? 'p-4' : 'p-3',
            bgClass,
            textColorClass
          )}
          onClick={() => onToggle(kategori.id)}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn('h-7 w-7 hover:bg-white/20', textColorClass)}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(kategori.id);
            }}
          >
            {apneKategorier.has(kategori.id) ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          <Icon className={iconSizeClass} />
          <span className={cn('flex-grow', level === 0 ? 'font-semibold' : 'font-medium')}>
            {kategori.navn}
          </span>
          {harChildren && (
            <Badge className="bg-white text-foreground hover:bg-white">{children.length} sub</Badge>
          )}
          {harTalenter && (
            <Badge className="bg-white text-foreground hover:bg-white">{selections.length} talenter</Badge>
          )}
          {erValgt && (
            <>
              <Badge className="bg-white text-secondary font-semibold hover:bg-white">
                {selectedCount} valgt
              </Badge>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn('h-7 w-7 hover:bg-white/20', textColorClass)}
                onClick={(e) => {
                  e.stopPropagation();
                  onClearKategori(kategori.id);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {apneKategorier.has(kategori.id) && (
          <div className="pl-8 mt-2">
            {/* Sub-kategorier (rekursivt) */}
            {children.map((child) => renderKategori(child, level + 1))}

            {/* Talenter for denne kategorien */}
            {harTalenter && (
              <SelectableTalentList
                selections={selections}
                onToggle={(index) => onTalentToggle(kategori.id, index)}
                onAntallChange={(index, antall) => onAntallChange(kategori.id, index, antall)}
                onBeskrivelseChange={(index, beskrivelse) =>
                  onBeskrivelseChange(kategori.id, index, beskrivelse)
                }
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const rootKategorier = kategorier.filter((k) => !k.parent_id);

  return <div>{rootKategorier.map((root) => renderKategori(root, 0))}</div>;
};

export default SelectableTalentTree;
